import { useState, useMemo } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons"
import { t } from "i18next"
import Tooltip from "@/components/tooltip"
import { RiskScaleItem } from "@/features/risks/entities/risk/model/risk.types"
import { getRiskHelpText } from "../utils/getRiskHelpText"
import { HelpKey, SimpleMetricKey } from "../model/risk.help.config"
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import riskStore from "../model/risk.store"
import { getGradientColorAt } from "../utils/getGradientColor.utils"

interface Props {
  id: SimpleMetricKey
  helpKey: HelpKey
}

export default function RiskScaleSelector({ id, helpKey }: Props) {
  const dispatch = useAppDispatch()
  const draft = useAppSelector(riskStore.selectors.draft)
  const [value, setValue] = useState<number>(draft[id])

  const scale: RiskScaleItem[] = draft[`${id}Scale`]
  const isReversed = helpKey === "p"

  /** Construction de l’échelle complète */
  const scaleExtended = useMemo(() => {
    const majorValues = scale.map((i) => i.value)
    const minValue = Math.min(...majorValues)
    const maxValue = Math.max(...majorValues)
    let lastLabel = scale[0]?.label || ""

    return Array.from({ length: maxValue - minValue + 1 }, (_, i) => {
      const current = i + minValue
      const found = scale.find((item) => item.value === current)
      if (found) lastLabel = found.label
      return { value: current, isMajor: Boolean(found), label: lastLabel }
    })
  }, [scale])

  /** Edition locale + dispatch global */
  const handleScaleChange = (val: number) => {
    setValue(val)
    dispatch(riskStore.actions.edit({ key: id, value: val }))
  }

  return (
    <div className="flex w-full items-center py-4 gap-4 h-[42px]">
      {/* Label gauche */}
      <div className="w-32 flex items-center gap-2">
        <span className="text-input-title grey-t font-bold">{t(`risk_item_${helpKey}`)}</span>
        <Tooltip text={getRiskHelpText(helpKey, t)} position="right">
          <FontAwesomeIcon className="cursor-help text-gray-500" icon={faInfoCircle} />
        </Tooltip>
      </div>

      {/* Barre + points */}
      <div className="relative flex-1 h-8 flex items-center max-w-[400px]">
        {/* Barre gradient */}
        <div
          className="absolute top-1/2 left-0 -translate-y-1/2 w-full h-[11px] rounded-full"
          style={{
            background: isReversed
              ? "linear-gradient(90deg,#EB5A46 0%,#FF9500 30%,#F2D600 60%,#20CA7E 100%)"
              : "linear-gradient(90deg,#20CA7E 0%,#F2D600 40%,#FF9500 70%,#EB5A46 100%)"
          }}
        />

        {/* Points */}
        <div className="relative flex w-full justify-between items-center gap-1">
          {scaleExtended.map((item, index) => {
            const isActive = value === item.value
            const position = index / (scaleExtended.length - 1)
            const color = getGradientColorAt(position, isReversed)
            const isDisplayedAsMajor = item.isMajor || isActive

            return (
              <Tooltip key={item.value} text={item.label} position="top">
                <div
                  onClick={() => handleScaleChange(item.value)}
                  className={`relative rounded-full transition-transform cursor-pointer flex items-center justify-center
                    ${isActive ? "h-8 w-8" : isDisplayedAsMajor ? "h-6 w-6" : "h-2 w-2"}
                    ${isActive ? "" : "hover:scale-110"}`}
                  style={{
                    border: `2px solid ${color}`,
                    backgroundColor: isActive ? color : "white"
                  }}>
                </div>
              </Tooltip>
            )
          })}
        </div>
      </div>

      {/* Label droite */}
      <div className="ml-4 text-sm font-medium flex items-center gap-4" style={{ width: "360px" }}>
        {(() => {
          const maxValue = Math.max(...scale.map((i) => i.value))
          const position = (value - 1) / (maxValue - 1)
          const color = getGradientColorAt(position, isReversed)
          const selectedScale = scaleExtended.find((item) => item.value === value)
          const selectedLabel = selectedScale?.label ?? value.toString()
          return (
            <>
              <span style={{ color, fontSize: "18px", fontWeight: "bold", width: "60px" }}>
                {value}/{maxValue}
              </span>
              <span className="text-gray-700" style={{ width: "280px" }}>
                {selectedLabel.replace(/\//g, " /​ ")}
              </span>
            </>
          )
        })()}
      </div>
    </div>
  )
}
